// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved

import React, { PureComponent, Fragment, createRef } from 'react'
import PropTypes from 'prop-types'
import { graphql, Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import * as Sentry from '@sentry/browser'
import { isEqual } from 'lodash'

import {
  SvgIcon,
  TextArea,
  RadioBoolean,
  Button,
  Modal,
  Warning,
  Spinner,
  Feedback,
  CheckboxToggle,
  Select,
  FileUpload
} from '../../'

import { Option } from './Option'

import {
  EMPLOYER_TWO_A_DAY,
  EMPLOYER_TWO_A_DAY_STG,
  EMPLOYER_MESSAGEPLUS,
  EMPLOYER_STRATHBREEDE,
  FILETYPE_WHATSAPP_MEDIA
} from '../../../constants'

import { getFileExtension } from '../../../utils'


const MUTATION_WHATSAPPMENU = gql`
  mutation($input: WhatsappMenuMutationInput!) {
    whatsappmenuMutate(input: $input) {
      whatsappmenu {
        id
        isPublished
        publishedSnapshot
        title
        menuitems {
          edges {
            node {
              id
              state
              optionOrder
              optionText
              contentEndpoint
            }
          }
        }
      }
    }
  }
`

const MUTATION_WHATSAPPMENUITEM = gql`
  mutation($input: WhatsappMenuItemMutationInput!) {
    whatsappmenuitemMutate(input: $input) {
      whatsappmenuitem {
        id
        state
        optionOrder
        optionText
        contentEndpoint
        whatsappmenu {
          id
          title
          publishedSnapshot
        }
      }
    }
  }
`

const INITIAL_STATE = {
  editMode: true,
  introParagraph: null,
  menuOptions: [],
  currentOptionInFocus: null,
  showDiscardConfirmModal: false,
  showSaveSuccessFeedback: false,
  showPublishSuccessFeedback: false,
  optionRefs: [],
  publishError: null,
  fileUploadFailed: false
}


class EmployerWhatsAppEditor extends PureComponent {
  static propTypes = {
    employerId: PropTypes.string.isRequired
  }

  state = INITIAL_STATE

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.employerId !== this.props.employerId) {
      // when employer accounts have been switched refetch data
      this.setState(INITIAL_STATE)
      this.props.data.refetch()
    }
    else if (this.props.data &&
      this.props.data.employer &&
      this.props.data.employer.whatsappmenu) {

      if (prevProps.data.networkStatus === 4 &&  // refetching (see https://github.com/apollographql/apollo-client/blob/master/src/core/networkStatus.ts)
        this.props.data.networkStatus === 7) {  // no request in flight, ready
        // data has been refetched after an employer account switch occurred
        this.setState(
          { introParagraph: this.props.data.employer.whatsappmenu.title },
          () => {
            if (this.props.data.employer.whatsappmenu.menuitems.edges.length >= 3) {
              this.sortMenuItemsAndSetState(
                this.props.data.employer.whatsappmenu.menuitems.edges.map(({ node: item }) => item),
                this.setOptionRefs
              )
            } else {
              this.setState({ menuOptions: [], optionRefs: [] })
            }
          }
        )
      }
      else if (prevProps.data.loading && !this.props.data.loading) {
        // data was first fetched after component mounted
        if (this.state.introParagraph === null &&
          this.props.data.employer &&
          this.props.data.employer.whatsappmenu
        ) {
          this.setState({ introParagraph: this.props.data.employer.whatsappmenu.title })
        }

        if (this.state.menuOptions.length === 0 &&
          this.props.data.employer &&
          this.props.data.employer.whatsappmenu &&
          this.props.data.employer.whatsappmenu.menuitems.edges.length >= 3) {
          this.sortMenuItemsAndSetState(
            this.props.data.employer.whatsappmenu.menuitems.edges.map(({ node: item }) => item),
            this.setOptionRefs
          )
        }

        if (prevState.menuOptions.length !== this.state.menuOptions.length) {
          this.setOptionRefs()
        }
      }
    }
  }

  setOptionRefs = () => {
    let optionRefs = []

    this.state.menuOptions
      .sort(this.sortAscendingByOptionOrder)
      .forEach(option => optionRefs.push(createRef()))

    this.setState({ optionRefs })
  }

  initialiseMenu = (whatsappmenuMutate, whatsappmenuitemMutate, refetch) => (
    whatsappmenuMutate({ variables: {
      input: {
        employer: this.props.employerId,
        title: '',
        description: 'Top-level menu',
        footer: '- Reply *PICSA* to go to Picsa main menu',
      }
    }})
    .then(({ data }) => {
      // create 3 draft menuitems and set state.menuOptions
      this.initialiseMenuItems(whatsappmenuitemMutate, data.whatsappmenuMutate.whatsappmenu.id)
      // refetch()
    })
    .catch(error => null)
  )

  initialiseMenuItems = (whatsappmenuitemMutate, menuId) => (
    [1,2,3,4,5].forEach(menuItemNumber => (
      whatsappmenuitemMutate({
        variables: {
          input: {
            optionText: '',
            optionOrder: menuItemNumber,
            state: 'draft',
            whatsappmenu: menuId
          }
        }
      })
      .catch(error => Sentry.captureMessage(`Failed to initialise employer WhatsApp menu - ${error}`))
    ))
  )

  sortAscendingByOptionOrder = (a, b) => (
    a.optionOrder < b.optionOrder
      ? -1
      : 1
  )

  sortMenuItemsAndSetState = (menuItems, callbackFunc = () => null) => {
    const sortedMenuItems = menuItems.sort(this.sortAscendingByOptionOrder)  // how can default order by done in backend?

    this.setState(
      {
        menuOptions: sortedMenuItems.map(item => (
          {
            id: item.id,
            text: item.optionText,
            state: item.state,
            active: !!(item.optionText && item.optionText.length > 0),  // TODO: review usage of 'active'
            contentEndpoint: JSON.parse(item.contentEndpoint)
          }
        ))
      },
      callbackFunc
    )
  }

  addOption = (optionNumber) => (
    this.setState(prevState => {
      const options = [...prevState.menuOptions]

      options[optionNumber - 1].active = true

      return { menuOptions: options }
    })
  )

  removeOption = (optionNumber) => {
    let index = optionNumber - 1

    this.setState(
      prevState => {
        const options = [...prevState.menuOptions]
        const option = options[index]

        if (options[index + 1] && options[index + 1].text && options[index + 1].text.length > 0) {
          // TODO complete the list reshuffling logic below
          // transfer values from next item in list, effectively moving list item into removed one's position
          option.text = options[index + 1].text
          option.contentEndpoint.type = options[index + 1].contentEndpoint.type
          option.contentEndpoint.content = options[index + 1].contentEndpoint.content
          option.state = options[index + 1].state
          // reset next list items' value as it has now moved up list order
          this.removeOption(optionNumber + 1)
        } else {
          option.text = ''
          option.contentEndpoint.type = 'text'
          option.contentEndpoint.content = ''
          option.active = false
          option.state = 'DRAFT'
        }

        return { menuOptions: options }
      },
      () => {
        this.setOptionRefs()
        this.setState({ currentOptionInFocus: null })
      }
    )
  }

  handleIntroParagraphChange = (e) => (
    e.target.value.length > 280
    ? this.setState({ introParagraphMaxLengthExceeded: true })
    : this.setState({
        introParagraph: e.target.value,
        introParagraphMaxLengthExceeded: false
      })
  )

  handleOptionTextChange = (optionNumber, newTextValue) => (
    this.setState(prevState => {
      const options = [...prevState.menuOptions]

      options[optionNumber - 1].text = newTextValue

      return { menuOptions: options }
    })
  )

  handleContentEndPointTextChange = (optionNumber, newTextValue) => (
    this.setState(prevState => {
      let options = [...prevState.menuOptions]

      options[optionNumber - 1].contentEndpoint.content = newTextValue

      return { menuOptions: options }
    })
  )

  handleContentEndPointTypeChange = (optionNumber, isFileType) => (
    this.setState(prevState => {
      let options = [...prevState.menuOptions]

      if (isFileType) {
        options[optionNumber - 1].contentEndpoint.type = 'text'
        options[optionNumber - 1].contentEndpoint.content = ''
      } else {
        options[optionNumber - 1].contentEndpoint.type = 'file'
        options[optionNumber - 1].contentEndpoint.content = {
          fileType: null,
          file: null
        }
      }

      return { menuOptions: options }
    })
  )

  handleContentEndPointFileTypeChange = (optionNumber, fileType) => (
    this.setState(prevState => {
      let options = [...prevState.menuOptions]

      options[optionNumber - 1].contentEndpoint.content = {
        fileType,
        file: null
      }

      return { menuOptions: options }
    })
  )

  handleContentEndPointFileChange = (optionNumber, fileId) => (
    this.setState(prevState => {
      let options = [...prevState.menuOptions]
      options[optionNumber - 1].contentEndpoint.content.file = fileId

      return { menuOptions: options }
    })
  )

  changeOptionInFocus = (optionNumber) => (
    this.setState({ currentOptionInFocus: optionNumber })
  )

  handleToggle = () => (
    this.setState(prevState => {
      if (prevState.editMode === true) {
        return { editMode: false, currentOptionInFocus: null }
      } else {
        return { editMode: true, currentOptionInFocus: null }
      }
    }
  ))

  showDiscardConfirmModal = () => this.setState({ showDiscardConfirmModal: true })

  discardAll = (whatsappmenuMutate, whatsappmenuitemMutate, menuId) => {
    const activeMenuItems = this.state.menuOptions.filter(option => option.active)
    for (let i = activeMenuItems.length; i > 0; i--) {
      this.removeOption(i)
    }
    const menuItemResetPromises = this.props.data.employer.whatsappmenu.menuitems.edges.map(({ node: item }) => (
      this.resetMenuItem(whatsappmenuitemMutate, item.id)
    ))
    Promise.all(menuItemResetPromises).then(() => {
      this.resetMenu(whatsappmenuMutate, whatsappmenuitemMutate, menuId)
       .then(() => this.setState(INITIAL_STATE))
    })
  }

  resetMenu = (whatsappmenuMutate, whatsappmenuitemMutate, menuId) => {
    let input = {
      id: menuId,
      employer: this.props.employerId,
      title: '',
      description: 'Top-level menu',
      footer: '- Reply *PICSA* to go to Picsa main menu',
      publishedSnapshot: "{\"menuitems\": {}}"
    }

    return whatsappmenuMutate({ variables: { input }})
  }

  resetMenuItem = (whatsappmenuitemMutate, itemId) => (
    whatsappmenuitemMutate({
      variables: {
        input: {
          id: itemId,
          optionText: '',
          state: 'draft',
          contentEndpoint: JSON.stringify({
            "type": 'text',
            "content": '',
            "variables": []
          })
        }
      }
    })
  )

  saveChanges = (whatsappmenuMutate, whatsappmenuitemMutate, currentSavedOptions, menuId) => {
    // save intro paragraph if specifed and/or changed
    if (this.props.data.employer.whatsappmenu.title !== this.state.introParagraph) {
      whatsappmenuMutate({
        variables: {
          input: {
            id: menuId,
            title: this.state.introParagraph
          }
        }
      })
    }
    // save menu item changes
    let mutationPromises = []
    this.state.menuOptions.forEach((option, index) => {
      if (currentSavedOptions[index].optionText !== option.text ||
        !isEqual(
          JSON.parse(currentSavedOptions[index].contentEndpoint).content,
          option.contentEndpoint.content
        )) {

        mutationPromises.push(
          whatsappmenuitemMutate({
            variables: {
              input: {
                id: option.id,
                optionText: option.text,
                optionOrder: index + 1,
                contentEndpoint: JSON.stringify(option.contentEndpoint),
                state: 'draft',
                whatsappmenu: menuId
              }
            }
          })
        )
      }
    })
    Promise.all(mutationPromises)
      .then(() => this.setState({ showSaveSuccessFeedback: true, publishError: null }))
      .catch(error => Sentry.captureMessage(`Failed to save employer WhatsApp menu changes - ${error}`))
  }

  undoChanges = () => {
    this.setState({ introParagraph: this.props.data.employer.whatsappmenu.title })
    this.sortMenuItemsAndSetState(
      this.props.data.employer.whatsappmenu.menuitems.edges.map(({ node: item }) => item
    ))
  }

  publish = (whatsappmenuMutate, whatsappmenuitemMutate, savedMenuItems) => {
    if (this.noChangesToMenuOptions(this.state.menuOptions, savedMenuItems) &&
      savedMenuItems.filter(item =>  item.state === "PUBLISHED").count > 0) {
      // no change made to menu options, but intro paragraph changed
      this.publishMenu(whatsappmenuMutate)
        .then(() => this.setState({ showPublishSuccessFeedback: true }))
    }
    else {
      // changes made to menu options and possibly to intro paragraph too
      this.publishMenuItems(whatsappmenuitemMutate)
        .then(() => (
          this.publishMenu(whatsappmenuMutate)
            .then(() => this.setState({ showPublishSuccessFeedback: true, publishError: null }))
            .catch(error => {
              this.setState({ publishError: error.message.replace('GraphQL error: ', '') })
            })
        ))
    }
  }

  publishMenu = (whatsappmenuMutate) => {
    const input = {
      id: this.props.data.employer.whatsappmenu.id,
      publish: true
    }

    return whatsappmenuMutate({ variables: { input }})
  }

  publishMenuItems = (whatsappmenuitemMutate) => {
    const itemsToPublish = this.props.data.employer.whatsappmenu.menuitems.edges.filter(({ node: item }) => {
      const contentEndpoint = JSON.parse(item.contentEndpoint)
      const contentEndpointType = contentEndpoint.type
      const contentEndpointContent = contentEndpoint.content
      return (
        item.optionText &&
        item.optionText.length > 0 &&
        item.state !== 'PUBLISHED' &&
        (
          (contentEndpointType === 'text' && contentEndpointContent.length > 0) ||
          (
            contentEndpointType === 'file' &&
            contentEndpointContent.fileType !== null &&
            (
              (
                contentEndpointContent.fileType !== 'Payslip' &&   // 'Media' file type
                contentEndpointContent.file !== null
              ) ||
              contentEndpointContent.fileType === 'Payslip'
            )
          )
        )
      )
    })

    const publishMutationPromises = itemsToPublish.map(({ node: item }) => (
      whatsappmenuitemMutate({
        variables: {
          input: {
            id: item.id,
            state: 'published'
          }
        }
      })
    ))

    return Promise.all(publishMutationPromises)
  }

  noChangesToIntroParagraph = () => (
    this.props.data.employer.whatsappmenu.title === this.state.introParagraph
  )

  noChangesToMenuOptions = (changedOptions, savedOptions) => {
    const isUnChanged = (option, index) => (
      // text unchanged?
      option.text === savedOptions[index].optionText &&
      // endpoint content unchanged?
      isEqual(
        option.contentEndpoint.content,
        JSON.parse(savedOptions[index].contentEndpoint).content
      )
    )
    return changedOptions.every(isUnChanged)
  }

  calcOptionCoords = (refIndex) => (
    this.state.optionRefs[refIndex].current.offsetTop + 25
  )

  showMenuItemConnector = (currentOptionInFocus) => {
    if (currentOptionInFocus !== null && this.state.editMode) {
      const index = currentOptionInFocus - 1
      return this.state.menuOptions[index].text && this.state.menuOptions[index].text.length > 0 &&
        <div className='whatsapp-editor-connector' style={{ top: this.calcOptionCoords(index) }} />
    }
    else {
      return null
    }
  }

  resetFileUploadFailed = () => this.state.fileUploadFailed
    ? this.setState({ fileUploadFailed: false })
    : null

  setFileUploadFailed = () => this.setState({ fileUploadFailed: true })

  render() {
    const { loading, error, employer } = this.props.data
    const { editMode, menuOptions, currentOptionInFocus, showDiscardConfirmModal, showSaveSuccessFeedback, showPublishSuccessFeedback } = this.state

    if (loading) {
      return null
    }

    if (error) {
      return null
    }

    if (employer.whatsappmenu === null) {
      return (
        <Mutation mutation={MUTATION_WHATSAPPMENU}>
          {whatsappmenuMutate => (
            <Mutation mutation={MUTATION_WHATSAPPMENUITEM}>
              {whatsappmenuitemMutate => (
                <Button
                  type='brand'
                  onClick={() => this.initialiseMenu(whatsappmenuMutate, whatsappmenuitemMutate)}
                  classNames='slds-m-top_x-large'>
                  Generate draft menu
                </Button>
              )}
            </Mutation>
          )}
        </Mutation>
      )
    }

    if (employer.whatsappmenu.menuitems.edges.length === 0) {
      return (
        <Mutation mutation={MUTATION_WHATSAPPMENUITEM}>
          {whatsappmenuitemMutate => (
            <Button
              type='brand'
              onClick={() => this.initialiseMenuItems(whatsappmenuitemMutate, employer.whatsappmenu.id)}
              classNames='slds-m-top_x-large'>
              Complete initialisation of draft menu
            </Button>
          )}
        </Mutation>
      )
    }

    if (menuOptions.length === 0) {
      return <Spinner type='tail' />
    }

    const savedMenuItems = employer.whatsappmenu.menuitems.edges
      .map(({ node: item }) => item)
      .sort(this.sortAscendingByOptionOrder)

    const publishedSnapshot = JSON.parse(employer.whatsappmenu.publishedSnapshot)
    const publishedSnapshotMenuItems = Object.entries(publishedSnapshot.menuitems).sort((a, b) => a[0].localeCompare(b[0])) || []

    const availableFileTypes = [EMPLOYER_TWO_A_DAY, EMPLOYER_TWO_A_DAY_STG, EMPLOYER_MESSAGEPLUS, EMPLOYER_STRATHBREEDE].indexOf(employer.id) > -1  // NOTE: temporary measure
      ? ['Payslip', 'Media']
      : ['Media']

    return (
      <div className='slds-size_1-of-1 slds-m-left_medium slds-m-right_medium slds-p-left_medium'>
        <div className='slds-grid slds-m-bottom_medium' style={{ alignItems: 'center' }}>
          <div className='u-font-size-large'>
            Current mode:
          </div>
          <RadioBoolean
            name='editOrPreview'
            firstLabel='Edit'
            secondLabel='Published'
            value={editMode}
            onChange={this.handleToggle}
            className='slds-p-left_large'
            labelClassName='u-font-size-larger'
          />
        </div>
        <div className='slds-grid slds-size_10-of-12 slds-p-around_large' style={currentOptionInFocus && menuOptions[0].text.length > 0 ? { justifyContent: 'space-between' } : null}>
          {this.showMenuItemConnector(currentOptionInFocus)}
          <div
            className='slds-p-around_large'
            style={{ width: 450, border: '2px solid darkgrey', borderRadius: '2%', backgroundColor: 'whitesmoke', boxShadow: '2px 3px 8px 0 rgba(0,0,0, .25)', zIndex: 2 }}>
            <div className='slds-grid slds-m-top_medium'>
              <div className='slds-size_1-of-1'>
                {/* intro paragrpah section */}
                {editMode
                  ? <div className='slds-m-bottom_medium'>
                      <TextArea
                        placeholder='Enter title or intro paragraph(s) (max 280 characters)'
                        value={this.state.introParagraph || ''}
                        rows={4}
                        onChange={this.handleIntroParagraphChange}
                        className='u-font-size-larger'
                        style={
                          this.state.introParagraphMaxLengthExceeded
                            ? {
                                border: '2px solid red',
                                paddingTop: '.75em',
                                paddingBottom: '.75em'
                              }
                            : {
                                paddingTop: '.75em',
                                paddingBottom: '.75em'
                              }
                        }
                      />
                      {this.state.introParagraphMaxLengthExceeded
                        ? <div style={{ fontSize: '.85em', color: 'red', textAlign: 'right' }}>
                            <Warning inline={true} iconSize='x-small'>
                              Max length for intro paragraph exceeded
                            </Warning>
                          </div>
                        : this.state.introParagraph && this.state.introParagraph.length > 0 &&
                            <div className='slds-text-color_weak' style={{ fontSize: '.85em', textAlign: 'right' }}>
                              <SvgIcon
                                variant='utility'
                                symbol='info'
                                size='x-small'
                                colour='light'
                                className='slds-p-right_x-small'
                              />
                              {`${280 - this.state.introParagraph.length} characters for intro paragraph left`}
                            </div>
                      }
                    </div>
                  : publishedSnapshot.intro &&
                      <p className='u-font-size-large'>
                        {publishedSnapshot.intro}
                      </p>
                }
                {/* menu options section */}
                {editMode
                  ? menuOptions.map((item, index) => {
                      const optionNumber = index + 1
                      const previousOption = index > 0
                        ? menuOptions[index - 1]
                        : null

                      return (
                        <div
                          className='slds-grid'
                          style={{ alignItems: 'baseline' }}
                          key={`edit_options_${index}_${item.state}`}
                          ref={this.state.optionRefs[index]}>
                          <Option
                            order={optionNumber}
                            text={item.text}
                            onChange={e => this.handleOptionTextChange(optionNumber, e.target.value)}
                            onClick={() => {
                              if (item.active === false) {
                                if (optionNumber === 1) {
                                  this.addOption(optionNumber)
                                } else if (previousOption.text && previousOption.text.length > 0) {
                                  this.addOption(optionNumber)
                                }
                              }
                              this.changeOptionInFocus(optionNumber)
                            }}
                            focus={() => this.changeOptionInFocus(optionNumber)}
                            editButton={editMode
                              ? item.text && item.text.length > 0
                                ? <SvgIcon
                                    variant='utility'
                                    symbol='dash'
                                    size='small'
                                    colour='error'
                                    onClick={() => this.removeOption(optionNumber)}
                                  />
                                : optionNumber === 1
                                  ? <SvgIcon
                                      variant='utility'
                                      symbol='add'
                                      size='small'
                                      colour='success'
                                    />
                                  : previousOption && previousOption.text && previousOption.text.length > 0
                                    ? <SvgIcon
                                        variant='utility'
                                        symbol='add'
                                        size='small'
                                        colour='success'
                                      />
                                    : null
                              : null
                            }
                            autoFocus={currentOptionInFocus === optionNumber && item.text && item.text.length > 0}
                            disabled={item.active === false}
                            active={currentOptionInFocus === optionNumber}
                            key={`option_${optionNumber}`}
                          />
                        </div>
                      )
                    })
                  : publishedSnapshotMenuItems.length > 0 &&
                    publishedSnapshotMenuItems.map(([optionText, contentEndPoint], index) => {
                      const optionNumber = index + 1
                      return (
                        <div
                          className='clickable u-font-size-large slds-p-around_small'
                          style={currentOptionInFocus === optionNumber
                            ? { borderLeft: '2px solid green', color: 'green' }
                            : null
                          }
                          onClick={() => this.changeOptionInFocus(optionNumber)}
                          key={`published_options_${optionText}_${index}`}>
                          {optionText}
                        </div>
                      )
                    })
                }
              </div>
            </div>
          </div>
          {/* content endpoint section */}
          {currentOptionInFocus &&
            menuOptions[currentOptionInFocus - 1] &&
            menuOptions[currentOptionInFocus - 1].text &&
            menuOptions[currentOptionInFocus - 1].text.length > 0 &&
            <div
              className='slds-m-left_xxx-large slds-p-top_large slds-p-left_x-large slds-p-right_x-large slds-p-bottom_x-large'
              style={{ width: 400, border: '2px solid darkgrey', borderRadius: '2%', backgroundColor: 'whitesmoke', boxShadow: '2px 3px 10px 0 rgba(0,0,0, .25)', zIndex: 2 }}>
              {editMode
                ? <Fragment>
                    <div className='slds-grid'>
                      <CheckboxToggle
                        id="toggleFileContentEndpoint"
                        offStateLabel='Text'
                        onStateLabel='File'
                        checked={menuOptions[currentOptionInFocus - 1].contentEndpoint.type === 'file'}
                        onChange={() => (
                          this.handleContentEndPointTypeChange(
                            currentOptionInFocus,
                            menuOptions[currentOptionInFocus - 1].contentEndpoint.type === 'file'
                          )
                        )}
                        className='slds-col_bump-left slds-m-bottom_small'
                      />
                    </div>
                    {menuOptions[currentOptionInFocus - 1].contentEndpoint.type === 'file'
                      ? <Select
                          value={menuOptions[currentOptionInFocus - 1].contentEndpoint.content.fileType}
                          placeholder='Please select file type'
                          options={availableFileTypes}
                          onChange={e => this.handleContentEndPointFileTypeChange(currentOptionInFocus, e.target.value)}
                          minWidth={150}
                        />
                      : <TextArea
                          placeholder='Enter message'
                          value={menuOptions[currentOptionInFocus - 1].contentEndpoint.content}
                          rows={5}
                          onChange={e => this.handleContentEndPointTextChange(currentOptionInFocus, e.target.value)}
                          className='slds-m-bottom_medium u-font-size-larger'
                          autoFocus={menuOptions[currentOptionInFocus - 1].contentEndpoint.content.length > 0}
                        />
                    }
                  </Fragment>
                : publishedSnapshotMenuItems.length > 0 &&
                    <div
                      className='slds-m-top_medium u-font-size-large'
                      style={
                        publishedSnapshotMenuItems[currentOptionInFocus - 1][1]["text"]
                          ? { whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }
                          : {}
                      }>
                      {publishedSnapshotMenuItems[currentOptionInFocus - 1][1]["text"]}
                    </div>
              }
              {editMode
                ? menuOptions[currentOptionInFocus - 1].contentEndpoint.type === 'file'
                  ? <div className='u-font-size-larger slds-m-top_xx-large'>
                      {menuOptions[currentOptionInFocus - 1].contentEndpoint.content.fileType
                        ? menuOptions[currentOptionInFocus - 1].contentEndpoint.content.fileType === "Payslip"
                          ? <div>
                              <SvgIcon
                                variant='doctype'
                                symbol='unknown'
                                size='large'
                                className='slds-p-right_small'
                              />
                              {`${menuOptions[currentOptionInFocus - 1].contentEndpoint.content.fileType} file will be send as response`}
                            </div>
                          : menuOptions[currentOptionInFocus - 1].contentEndpoint.content.file
                            // TODO: allow for file preview and alternate upload
                            ? <Fragment>
                                <div>
                                  <SvgIcon
                                    variant='doctype'
                                    symbol='unknown'
                                    size='large'
                                    className='slds-p-right_small'
                                  />
                                  {`${menuOptions[currentOptionInFocus - 1].contentEndpoint.content.fileType} file uploaded as response`}
                                </div>
                                <div className='slds-m-top_medium slds-p-around_small u-bg-color-white slds-border_top slds-border_bottom slds-text-align_center'>
                                  <Query
                                    query={gql`
                                      query($id: ID!) {
                                        file(id: $id) {
                                          id
                                          asset
                                          url
                                        }
                                      }`
                                    }
                                    variables={{ id: menuOptions[currentOptionInFocus - 1].contentEndpoint.content.file }}>

                                    {({ loading, error, data }) => {
                                      if (loading) {
                                        return <Spinner type='tail' />
                                      }

                                      if (error) {
                                        return (
                                          <div className='slds-size_1-of-1 slds-align_absolute-center' style={{ height: '150px', flexDirection: 'column' }}>
                                            <SvgIcon
                                              variant='utility'
                                              symbol='image'
                                              size='x-large'
                                              colour='error'
                                              className='slds-m-bottom_x-small slds-text-align_center'
                                            />
                                            Failed to fetch preview file
                                          </div>
                                        )
                                      }

                                      if (data.file === null) {
                                        return (
                                          <div className='slds-size_1-of-1 slds-align_absolute-center' style={{ height: '150px', flexDirection: 'column' }}>
                                            <SvgIcon
                                              variant='utility'
                                              symbol='image'
                                              size='x-large'
                                              colour='warning'
                                              className='slds-m-bottom_x-small slds-text-align_center'
                                            />
                                            No file found to preview
                                          </div>
                                        )
                                      }

                                      const fileExtension = getFileExtension(data.file.asset)
                                      const imageFormats = ['png', 'jpg', 'jpeg']
                                      const audioFormats = ['mp3', 'm4a']
                                      const videoFormats = ['mp4']

                                      if (imageFormats.indexOf(fileExtension) > -1) {
                                        return (
                                          <figure className='slds-file'>
                                            <img
                                              src={data.file.url}
                                              alt={data.file.asset}
                                              style={{ width: '100%' }}
                                            />
                                            {/* <FileCaption file={data.file} /> */}
                                          </figure>
                                        )
                                      }
                                      else if (audioFormats.indexOf(fileExtension) > -1) {
                                        return (
                                          <audio controls>
                                            <source src={data.file.url} preload="auto" width='100%' />
                                            Your browser does not support the audio tag.
                                          </audio>
                                        )
                                      }
                                      else if (videoFormats.indexOf(fileExtension) > -1) {
                                        return (
                                          <video controls style={{ width: '100%' }}>
                                            <source src={data.file.url} preload="auto" />
                                            Your browser does not support the video tag.
                                          </video>
                                        )
                                      }
                                      else {
                                        return 'Unsupported file format'
                                      }
                                    }}
                                  </Query>
                                </div>
                              </Fragment>
                            : <Fragment>
                                <FileUpload
                                  heading='Upload required file'
                                  endpoint='/v1/file/'
                                  onFileSelect={this.resetFileUploadFailed}
                                  onUploaded={fileId => {
                                    this.resetFileUploadFailed()
                                    this.handleContentEndPointFileChange(currentOptionInFocus, fileId)
                                  }}
                                  onFailed={this.setFileUploadFailed}
                                  filetype={FILETYPE_WHATSAPP_MEDIA}
                                  fileRelationships={[]}
                                />
                                {this.state.fileUploadFailed &&
                                  <div className='slds-m-top_large slds-m-left_small slds-text-color_error slds-grid' style={{ alignContent: 'center' }}>
                                    <SvgIcon
                                      variant='utility'
                                      symbol='warning'
                                      colour='error'
                                      size='small'
                                      className='slds-p-right_small'
                                    />
                                    File upload failed
                                  </div>
                                }
                              </Fragment>
                        : null
                      }
                    </div>
                  : <div className='u-font-size-large slds-m-top_medium'>
                      - *MENU* to go back
                    </div>
                : publishedSnapshotMenuItems[currentOptionInFocus - 1][1].hasOwnProperty('file')
                  ? <div className='u-font-size-larger slds-m-top_xx-large'>
                      {publishedSnapshotMenuItems[currentOptionInFocus - 1][1]["file"]["fileType"] !== null
                        ? publishedSnapshotMenuItems[currentOptionInFocus - 1][1]["file"]["fileType"] === "Payslip"
                          ? <div>
                              <SvgIcon
                                variant='doctype'
                                symbol='unknown'
                                size='large'
                                className='slds-p-right_small'
                              />
                              {`${publishedSnapshotMenuItems[currentOptionInFocus - 1][1]["file"]["fileType"]} file will be send as response`}
                            </div>
                          : <Fragment>
                              <div>
                                <SvgIcon
                                  variant='doctype'
                                  symbol='unknown'
                                  size='large'
                                  className='slds-p-right_small'
                                />
                                {`${publishedSnapshotMenuItems[currentOptionInFocus - 1][1]["file"]["fileType"]} file uploaded as response`}
                              </div>
                              <div className='slds-m-top_medium slds-p-around_small u-bg-color-white slds-border_top slds-border_bottom slds-text-align_center'>
                                <Query
                                  query={gql`
                                    query($id: ID!) {
                                      file(id: $id) {
                                        id
                                        asset
                                        url
                                      }
                                    }`
                                  }
                                  variables={{ id: publishedSnapshotMenuItems[currentOptionInFocus - 1][1]["file"]["file"] }}>

                                  {({ loading, error, data }) => {
                                    if (loading) {
                                      return <Spinner type='tail' />
                                    }

                                    if (error) {
                                      return (
                                        <div className='slds-size_1-of-1 slds-align_absolute-center' style={{ height: '150px', flexDirection: 'column' }}>
                                          <SvgIcon
                                            variant='utility'
                                            symbol='image'
                                            size='x-large'
                                            colour='error'
                                            className='slds-m-bottom_x-small slds-text-align_center'
                                          />
                                          Failed to fetch preview file
                                        </div>
                                      )
                                    }

                                    if (data.file === null) {
                                      return (
                                        <div className='slds-size_1-of-1 slds-align_absolute-center' style={{ height: '150px', flexDirection: 'column' }}>
                                          <SvgIcon
                                            variant='utility'
                                            symbol='image'
                                            size='x-large'
                                            colour='warning'
                                            className='slds-m-bottom_x-small slds-text-align_center'
                                          />
                                          No file found to preview
                                        </div>
                                      )
                                    }

                                    const fileExtension = getFileExtension(data.file.asset)
                                    const imageFormats = ['png', 'jpg', 'jpeg']
                                    const audioFormats = ['mp3', 'm4a']
                                    const videoFormats = ['mp4']

                                    if (imageFormats.indexOf(fileExtension) > -1) {
                                      return (
                                        <figure className='slds-file'>
                                          <img
                                            src={data.file.url}
                                            alt={data.file.asset}
                                            style={{ width: '100%' }}
                                          />
                                          {/* <FileCaption file={data.file} /> */}
                                        </figure>
                                      )
                                    }
                                    else if (audioFormats.indexOf(fileExtension) > -1) {
                                      return (
                                        <audio controls>
                                          <source src={data.file.url} preload="auto" width='100%' />
                                          Your browser does not support the audio tag.
                                        </audio>
                                      )
                                    }
                                    else if (videoFormats.indexOf(fileExtension) > -1) {
                                      return (
                                        <video controls style={{ width: '100%' }}>
                                          <source src={data.file.url} preload="auto" />
                                          Your browser does not support the video tag.
                                        </video>
                                      )
                                    }
                                    else {
                                      return 'Unsupported file format'
                                    }
                                  }}
                                </Query>
                              </div>
                            </Fragment>
                        : null
                      }
                    </div>
                  : <div className='u-font-size-large slds-m-top_medium'>
                      - *MENU* to go back
                    </div>
              }
            </div>
          }
        </div>
        <div className='slds-grid slds-m-top_x-large' style={{ alignItems: 'baseline' }}>
          <Button
            onClick={this.showDiscardConfirmModal}
            disabled={menuOptions[0].text.length === 0}
            minWidth={200}>
            Discard all
          </Button>
          {/* add button to discard only unpublished/recent changes */}
          {editMode &&
            <Fragment>
              <Mutation mutation={MUTATION_WHATSAPPMENU}>
                {whatsappmenuMutate => (
                  <Mutation mutation={MUTATION_WHATSAPPMENUITEM}>
                    {whatsappmenuitemMutate => (
                      <div>
                        <Button
                          onClick={() => {
                            this.saveChanges(
                              whatsappmenuMutate,
                              whatsappmenuitemMutate,
                              savedMenuItems,  // current saved options
                              employer.whatsappmenu.id
                            )
                          }}
                          disabled={
                            this.noChangesToIntroParagraph() &&
                            this.noChangesToMenuOptions(menuOptions, savedMenuItems)
                          }
                          minWidth={200}>
                          Save changes
                        </Button>
                        {(this.noChangesToIntroParagraph() === false ||
                          this.noChangesToMenuOptions(menuOptions, savedMenuItems) === false) &&
                          <Fragment>
                            <br />
                            <Button onClick={this.undoChanges} minWidth={200}>
                              Undo changes
                            </Button>
                          </Fragment>
                        }
                      </div>
                    )}
                  </Mutation>
                )}
              </Mutation>
              <Mutation mutation={MUTATION_WHATSAPPMENU}>
                {whatsappmenuMutate => (
                  <Mutation mutation={MUTATION_WHATSAPPMENUITEM}>
                    {whatsappmenuitemMutate => (
                      <Button
                        type='brand'
                        onClick={() => this.publish(whatsappmenuMutate, whatsappmenuitemMutate, savedMenuItems)}
                        disabled={
                          (
                            // all menu items unspecified
                            savedMenuItems.every(item => (
                              !!item.optionText === false  // '' or null
                            )) ||
                            // a saved menu item in 'draft' state exists with unspecified endpoint content
                            savedMenuItems.some(item => {
                              const contentEndpoint = JSON.parse(item.contentEndpoint)
                              const contentEndpointType = contentEndpoint.type
                              const contentEndpointContent = contentEndpoint.content
                              return (
                                item.state === 'DRAFT' &&
                                item.optionText &&
                                item.optionText.length > 0 &&
                                (
                                  (contentEndpointType === 'text' && contentEndpointContent.length === 0) ||
                                  (
                                    contentEndpointType === 'file' &&
                                    contentEndpointContent.fileType !== 'Payslip' &&
                                    contentEndpointContent.file === null
                                  )
                                )
                              )
                            })
                          ) ||
                          (
                            // intro paragraph unchanged
                            (
                              publishedSnapshot.hasOwnProperty('intro') && // intro paragraph exists in published snapshot
                              publishedSnapshot.intro === employer.whatsappmenu.title // saved intro paragraph matches what is in snapshot
                            ) &&
                            // there is nothing published that has changed
                            publishedSnapshotMenuItems.find((publishedItem, index) => {
                              let savedMenuItemContentEndpoint = JSON.parse(savedMenuItems[index].contentEndpoint)
                              return (
                                publishedItem[0] !== `- Reply *${index+1}* for ${savedMenuItems[index].optionText}` ||
                                Object.keys(publishedItem[1])[0] !== savedMenuItemContentEndpoint.type ||
                                !isEqual(
                                  Object.values(publishedItem[1])[0],
                                  savedMenuItemContentEndpoint.content
                                )
                              )
                            }) === undefined &&
                            // there is nothing new to be published
                            !savedMenuItems.some(item => {
                              const contentEndpoint = JSON.parse(item.contentEndpoint)
                              const contentEndpointType = contentEndpoint.type
                              const contentEndpointContent = contentEndpoint.content
                              return (
                                item.state === 'DRAFT' &&
                                item.optionText &&
                                item.optionText.length > 0 &&
                                (
                                  (contentEndpointType === 'text' && contentEndpointContent.length > 0) ||
                                  (
                                    contentEndpointType === 'file' &&
                                    contentEndpointContent.fileType !== null &&
                                    (
                                      (
                                        contentEndpointContent.fileType !== 'Payslip' &&   // 'Media' file type
                                        contentEndpointContent.file !== null
                                      ) ||
                                      contentEndpointContent.fileType === 'Payslip'
                                    )
                                  )
                                )
                              )
                            })
                          )
                        }
                        minWidth={200}
                        className='slds-p-left_medium'
                        style={this.state.publishError ? { border: '2px solid red'} : {}}>
                        Publish
                      </Button>
                    )}
                  </Mutation>
                )}
              </Mutation>
              {this.state.publishError &&
                <div className='slds-m-left_small slds-text-color_error'>
                  {this.state.publishError}
                </div>
              }
            </Fragment>
          }
        </div>
        {showDiscardConfirmModal &&
          <Modal
            heading='Confirm'
            body={
              <div className='slds-grid' style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <div>
                  <Warning className='u-font-size-larger'>
                    Are you sure you want to clear all?
                  </Warning>
                </div>
                <div className='slds-text-body_small'>
                  (any published content will be lost)
                </div>
              </div>
            }
            footer={
              <div className='slds-align_absolute-center'>
                <Button
                  minWidth={150}
                  onClick={() => this.setState({ showDiscardConfirmModal: false })}>
                  No
                </Button>
                <Mutation mutation={MUTATION_WHATSAPPMENU}>
                  {whatsappmenuMutate => (
                    <Mutation mutation={MUTATION_WHATSAPPMENUITEM}>
                      {whatsappmenuitemMutate => (
                        <Button
                          minWidth={150}
                          onClick={() => this.discardAll(whatsappmenuMutate, whatsappmenuitemMutate, employer.whatsappmenu.id)}>
                          Yes
                        </Button>
                      )}
                    </Mutation>
                  )}
                </Mutation>
              </div>
            }
          />
        }
        {showSaveSuccessFeedback &&
          <Feedback
            message='Saved'
            fadeDuration={2500}  // 2.5s matches .feedback-fade css class animation
            unmount={() => this.setState({ showSaveSuccessFeedback: false })}
            icon={
              <SvgIcon
                variant='utility'
                symbol='check'
                colour='inverse'
                className='slds-align_absolute-center'
              />
            }
          />
        }
        {showPublishSuccessFeedback &&
          <Feedback
            message='Published'
            fadeDuration={2500}
            unmount={() => this.setState({ showPublishSuccessFeedback: false })}
            icon={
              <SvgIcon
                variant='utility'
                symbol='check'
                colour='inverse'
                className='slds-align_absolute-center'
              />
            }
          />
        }
      </div>
    )
  }
}


const getEmployerWhatsAppMenu = gql`
  query($id: ID!) {
    employer(id: $id) {
      id
      whatsappmenu {
        id
        isPublished
        publishedSnapshot
        title
        menuitems {
          edges {
            node {
              id
              state
              optionOrder
              optionText
              contentEndpoint
            }
          }
        }
      }
    }
  }
`

const withData =
  graphql(getEmployerWhatsAppMenu, {
    options: ({ employerId }) => ({
      variables: {
        id: employerId
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true
    })
  })

export default withData(EmployerWhatsAppEditor)



// WEBPACK FOOTER //
// ./src/components/messageplus/employer-whatsapp-editor/index.js